"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  parseUnits,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import {
  PostMarketOrderResponse,
  Trade,
  TradeRequest,
  TradeResponse,
} from "#/types/orders";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { getDirectionIndex } from "#/utils/marketActionConverter";
import { extractOrderSize } from "#/utils/orderbook.utils";
import { EIP712Domain } from "#/utils/signature";

import { postMarketOrder } from "../kyan.service";
import {
  createMarketOrderEventHandlers,
  defaultApiResponseHandler,
} from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

const UserMarketOrder: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "marketOrder", type: "OrderTyped" },
  { name: "limitPrice", type: "uint256" },
  { name: "taker", type: "address" },
];

const OrderTyped: TypedDataParameter[] = [
  { name: "instrumentName", type: "string" },
  { name: "size", type: "uint256" },
  { name: "direction", type: "uint8" },
];

const signMarketOrder = async (
  trade: Trade,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<TradeRequest> => {
  const account = walletClient.account.address;
  const signature_deadline = getSignatureDeadline();

  try {
    const signature = await walletClient.signTypedData({
      account,
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { OrderTyped, UserMarketOrder },
      primaryType: "UserMarketOrder",
      message: {
        deadline: signature_deadline,
        marketOrder: {
          instrumentName: trade.market_order.instrument_name,
          size: parseUnits(extractOrderSize(trade.market_order).toString(), 6),
          direction: getDirectionIndex(trade.market_order.direction),
        },
        taker: trade.taker,
        limitPrice: parseUnits(trade.limit_price.toString(), 6),
      },
    });

    return {
      ...trade,
      signature_deadline,
      signature,
    };
  } catch (err) {
    console.error("Error signing market orders:", err);
    if (String(err).includes("chainId should be same as current chainId")) {
      throw new Error(
        `Wrong chainId on signature: ${await walletClient.getChainId()}`,
      );
    }
    if (String(err).includes("UserRejectedRequestError")) {
      throw new Error("Market order signature rejected");
    }

    throw new Error("Error signing market order");
  }
};

const validateMarketOrderSignature = async (
  trade: TradeRequest,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return await publicClient.verifyTypedData({
    address: trade.taker,
    domain: EIP712Domain[chainId],
    types: { OrderTyped, UserMarketOrder },
    primaryType: "UserMarketOrder",
    message: {
      deadline: trade.signature_deadline,
      marketOrder: {
        instrumentName: trade.market_order.instrument_name,
        size: parseUnits(extractOrderSize(trade.market_order).toString(), 6),
        direction: getDirectionIndex(trade.market_order.direction),
      },
      taker: trade.taker,
      limitPrice: parseUnits(trade.limit_price.toString(), 6),
    },
    signature: trade.signature,
  });
};

export const usePostMarketOrder = (
  options?: UseMutationOptions<
    Pick<PostMarketOrderResponse, "instrument_name" | "direction">,
    Error,
    Trade
  >,
) => {
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async (trade: Trade) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const serviceName = "Post Market Order";
      let response: TradeResponse;

      if (shouldUseSessionHash) {
        response = await defaultApiResponseHandler({
          serviceName,
          payload: { data: trade, includeSessionHash: true },
          service: postMarketOrder,
          ...createMarketOrderEventHandlers(serviceName),
        });
      } else {
        const signedTrade = await signMarketOrder(
          trade,
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        );

        const isSignatureValid = await validateMarketOrderSignature(
          signedTrade,
          walletClient.chain?.id as ChainId,
        );

        response = await defaultApiResponseHandler({
          serviceName,
          payload: { data: signedTrade },
          service: postMarketOrder,
          validation: [isSignatureValid],
          ...createMarketOrderEventHandlers(serviceName),
        });
      }

      return {
        instrument_name: response.fillResult.instrument_name,
        direction: response.fillResult.direction,
      };
    },
  });
};
